<template>
  <div id="AccountEndpoints" class="Main-Content">
    <ValidationObserver ref="form" v-slot="{ invalid }" slim>
      <LoadingLayout v-if="loading" :page="true" />

      <Page
        v-else
        :title="$t('Endpoints')"
        :secondaryActions="[{
          label: $t('Notes'),
          onAction: () => eventHub.$emit('showAccountNotes', 'ENDPOINT')
        }]"
      >
      
        <Layout>
          <LayoutSection>
            <Sheet
              :title="$t('Edit Endpoint')"
              :width="700"
              :visible="!!editId"
              @close="cancelEdit"
              padded
            >
              <template v-slot:actions>
                <Button v-if="editType === 'ftp'" :disabled="invalid || saving" type="primary" @click="save">{{ isCreating ? $t('Create') : $t('Update') }}</Button>
                <Button v-if="editType === 'esp'" :disabled="!isValid || invalid || saving" type="primary" @click="save">{{ isCreating ? $t('Create') : $t('Update') }}</Button>
                <Button v-if="editType === 'http'" :disabled="invalid || saving" type="primary" @click="save">{{ isCreating ? $t('Create') : $t('Update') }}</Button>
              </template>
              <Card 
                v-if="editType === 'esp'"
                :title="$t('Edit ESP Endpoint')"
              >
                <CardSection>
                  <FormLayout>
                    <validation-provider :name="$t('Name')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Name')"
                        v-model="configAccess.name"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <Select v-if="!isEditingCustom"
                      :label="$t('AccountEndpoints.Email Service Provider')"
                      :placeholder="$t('AccountEndpoints.Choose a provider')"
                      v-model="configAccess.espName"
                      :options="espOptions"
                    />
                  </FormLayout>
                </CardSection>
                <CardSection 
                  :title="$t('Configuration')"
                  :actions="[
                    {
                      label: $t('AccountEndpoints.View integration instructions'),
                      onAction: () => goToDocUrl()
                    }
                  ]"
                  v-if="provider"
                >
                  <!-- <a v-if="provider && provider.docUrl" :href="provider.docUrl" target="_blank" style="float: right; padding-top: 8px">View integration instructions →</a> -->
                  <FormLayout>
                    <template v-for="field in provider.fields">
                      <Select 
                        v-if="field.options"
                        :key="field.key"
                        :label="$t(field.label || '')"
                        v-model="config[field.key]"
                        :help-text="getFieldHint(field)"
                        :options="field.options"
                      />
                      <TextField 
                        v-else
                        :key="field.key"
                        :label="$t(field.label || '')"
                        v-model="config[field.key]" 
                        :help-text="$t(field.hint || '')"
                        :type="getFieldType(field)"
                      />
                    </template>
                    <ButtonGroup>
                      <Button :disabled="!isValid || isTesting || saving" @click="test">{{ $t('AccountEndpoints.Test Configuration') }}</Button>
                      <Spinner v-if="isTesting" size="small" color="blue" />
                    </ButtonGroup>
                  </FormLayout>


                </CardSection>
              </Card>
              <Card
                v-if="editType === 'http'"
                :title="$t('AccountEndpoints.Edit HTTP Endpoint')"
              >
                <CardSection>
                  <FormLayout>
                    <validation-provider :name="$t('Name')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Name')"
                        v-model="configAccess.name"
                        :error="errors[0]"
                      />
                    </validation-provider>
                  </FormLayout>
                </CardSection>
                <CardSection
                  :title="$t('Configuration')"
                >
                  <FormLayout>
                    <validation-provider :name="$t('URL')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('URL')"
                        v-model="configAccess.url"
                        :error="errors[0]"
                      />
                    </validation-provider>
                  </FormLayout>
                  
                </CardSection>
              </Card>
              <Card
                v-if="editType === 'ftp'"
                :title="$t('AccountEndpoints.Edit FTP Endpoint')"
              >
                <CardSection>
                  <FormLayout>
                    <validation-provider :name="$t('Name')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Name')"
                        v-model="configAccess.name"
                        :error="errors[0]"
                      />
                    </validation-provider>
                  </FormLayout>
                </CardSection>
                <CardSection
                  :title="$t('Configuration')"
                >
                  <FormLayout>
                    <validation-provider :name="$t('Hostname')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Hostname')"
                        v-model="configAccess.hostname"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('Username')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Username')"
                        v-model="configAccess.username"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('Password')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Password')"
                        type="password"
                        v-model="configAccess.password"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('Directory')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Directory')"
                        v-model="configAccess.directory"
                        :error="errors[0]"
                      />  
                    </validation-provider>
                    <validation-provider :name="$t('Update Speed')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        :label="$t('Update Speed')"
                        v-model="configAccess.updateSpeed"
                        :error="errors[0]"
                      />
                    </validation-provider>
                  </FormLayout>
                </CardSection>
              </Card>
            </Sheet>
            <Card 
              :title="$t('AccountEndpoints.ESP Endpoints')"
              :actions="[
                {
                  label: $t('AccountEndpoints.Add ESP Endpoint'),
                  onAction: () => startEdit('esp', -1)
                }
              ]"
            >
              <DataTable
                :columns="[
                  {
                    key: 'integrationName',
                    label: $t('AccountEndpoints.Integrations'),
                    fixed: true
                  },
                  {
                    key: 'providerName',
                    label: $t('AccountEndpoints.Provider')
                  }
                ]"
                :rows="formattedEspConfigs"
                :emptyText="$t('AccountEndpoints.No ESP Configs')"
              >
                <template v-slot:col:integrationName="{row}">
                  <PageLink @click="startEdit('esp', parseInt(row.id))">{{row.integrationName}}</PageLink>
                </template>
              </DataTable>
            </Card>
            <Card 
              :title="$t('AccountEndpoints.HTTP Endpoints')"
              :actions="[
                {
                  label: $t('AccountEndpoints.Add HTTP Endpoint'),
                  onAction: () => startEdit('http', -1)
                }
              ]"
            >
              <DataTable
                :columns="[
                  {
                    key: 'integrationName',
                    label: $t('AccountEndpoints.Integrations'),
                    fixed: true
                  },
                  {
                    key: 'url',
                    label: $t('URL')
                  }
                ]"
                :rows="formattedHttpConfigs"
                :emptyText="$t('AccountEndpoints.No HTTP Configs')"
              >
                <template v-slot:col:integrationName="{row}">
                  <PageLink @click="startEdit('http', parseInt(row.id))">{{row.integrationName}}</PageLink>
                </template>
              </DataTable>
            </Card>
            <Card
              :title="$t('AccountEndpoints.FTP Endpoints')"
              :actions="[
                {
                  label: $t('AccountEndpoints.Add FTP Endpoint'),
                  onAction: () => startEdit('ftp', -1)
                }
              ]"
            >
              <DataTable
                :columns="[
                  {
                    key: 'integrationName',
                    label: $t('AccountEndpoints.Integrations'),
                    fixed: true
                  },
                  {
                    key: 'url',
                    label: $t('URL')
                  }
                ]"
                :rows="formattedFtpConfigs"
                :emptyText="$t('AccountEndpoints.No FTP Configs')"
              >
                <template v-slot:col:integrationName="{row}">
                  <PageLink @click="startEdit('ftp', parseInt(row.id))">{{row.integrationName}}</PageLink>
                </template>
              </DataTable>
            </Card>
          </LayoutSection>
        </Layout>


      </Page>
    </ValidationObserver>

  </div>
</template>

<script>
import _ from 'lodash'
// import { extend as ValidateExtend } from 'vee-validate/dist/vee-validate.full'

export default {
  name: 'AccountEndpoints',
  data () {
    return {
      loading: true,
      saving: false,
      editId: null,
      editType: null,
      config: null,
      configCopy: null,
      endpoints: null,
      providers: null,
      isTesting: false
    }
  },
  mounted() {

    NProgress.start()
    Promise.all([this.api.getEspProviders(), this.api.getEndpoints()])
      .then((data) => { 
        const [ providers, endpoints ] = data
        this.providers = providers
        this.endpoints = endpoints
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
        
        // PROBABLY SHOULD BE DONE DIFFERENTLY
        // React to the provider being changed for an edited config.
        // Also process once initially (`immediate`) to initialize config fields.
        this.$watch('config.espName', function (id) {
          var provider = this.providers[id];
          if (provider) {
            // Create a new config object with all provider fields to ensure
            // they are reactive.
            var current = this.config;
            var config = { espName: provider.id };
            provider.fields.forEach(function (field) {
              var defaultValue = field.defaultValue;
              if (typeof defaultValue === 'undefined') {
                if (field.options) {
                  defaultValue = field.options[0].value;
                } else {
                  defaultValue = null;
                }
              }
              // Copy any common field values.
              config[field.key] = current[field.key] || defaultValue;
            });
            // Ensure `name` is reactive.
            config.name = this.config.name || '';
            // Replace config object.
            this.config = config;
          }
        }, { immediate: true });
      })
  },
  methods: {
    startEdit(type, id) {
      this.editId = id
      this.editType = type
      this.editIdChange(id)
    },
    async cancelEdit() {
      if (!_.isEqual(this.config, this.configCopy)) {
        const confirm = await this.$ui.confirm({
          title: this.$t('Unsaved Changes'),
          content: this.$t('AccountEndpoints.There are unsaved changes in this endpoint'),
          primaryActionLabel: this.$t('Lose changes and return to list'),
          primaryActionType: 'destructive',
          secondaryActionLabel: this.$t('Continue editing')
        })
        if (!confirm) {
          return
        }
      }
      this.editId = null
      this.editType = null
      this.editIdChange(null)
    },
    editIdChange(id) {
      if (id === -1) {
        this.config = {}
      } else if (id > 0) {
        switch (this.editType) {
          case 'esp': this.config = this.espConfigs[id]; break;
          case 'http': this.config = this.httpConfigs[id]; break;
          case 'ftp': this.config = this.ftpConfigs[id]; break;      
        }
      } else {
        this.config = null
      }
      this.configCopy = _.cloneDeep(this.config)
    },
    getProviderLabel(id) {
      if (id === 'CUSTOM') {
        return this.$t('Custom')
      }
      return this.providers[id] && this.providers[id].label && this.$t(this.providers[id].label)
    },
    getFieldHint(field) {
      var optHint;
      var value = this.config[field.key];
      for (var i = 0, len = field.options.length; i < len; i++) {
        var opt = field.options[i];
        if (opt.value === value) {
          optHint = opt.hint && this.$t(opt.hint)
          break;
        }
      }
      return optHint || field.hint || '';
    },
    getFieldType(field) {
      if (field.type === 'password') {
        return 'password';
      }
      return 'text';
    },
    validateField(field, value) {
      if (field.format instanceof RegExp) {
        return field.format.test(value);
      }
      if (field.required && !value) {
        return false;
      }
      return true;
    },
    goToDocUrl() {
      if (this.provider && this.provider.docUrl) {
        window.open(this.provider.docUrl, '_blank')
      }
    },  
    checkNameConflict() {
      if (this.nameConflict) {
        console.log('X')
        this.$refs.form.setErrors({
          xd: ['Name already used']
        })
      }
    },
    save() {
      if (this.nameConflict) {
        this.$ui.alert({
          title: this.$t('Error'),
          content: this.$t('AccountEndpoints.The name of this endpoint conflicts with another endpoint name'),
          primaryActionLabel: this.$t('Okay')
        })
        return
      }
      NProgress.start()
      this.saving = true
      this.api.saveEndpoint({
        id: this.isCreating ? null : parseInt(this.editId),
        type: this.editType.toUpperCase(),
        config: JSON.stringify(this.config)
      })
        .then((response) => {
          // console.log(response)
          if (response > 1) {
            this.endpoints.push({
              id: response,
              type: this.editType.toUpperCase(),
              config: JSON.stringify(this.config)
            })
          } else {
            const endpoint = this.endpoints.find(e => e.id === parseInt(this.editId))
            endpoint.config = JSON.stringify(this.config)
          }
          this.config = null
          this.configCopy = null
          this.editId = null
          this.editType = null
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    test() {
      this.isTesting = true
      fetch(`https://api.fanplayr.com/espTest?config=${encodeURIComponent(JSON.stringify(this.config))}&accountId=${this.theUser.onBehalfOfAccountId}`, {
      // fetch(`//10.1.10.240:8080/FanplayrBackend/espTest?config=${encodeURIComponent(JSON.stringify(this.config))}&accountId=${this.theUser.onBehalfOfAccountId}`, {
        method: 'get',
        mode: 'cors'
      })
      .then((res) => {
        return res.json()
      })
      .then((res) => {
        if (res.status === 'SUCCESS') {
          this.$ui.alert({
            title: this.$t('Error'),
            content: this.$t('AccountEndpoints.successMessage')
          })
        } else {
          this.$ui.alert({
            title: this.$t('Error'),
            content: this.$t('AccountEndpoints.failedMessage')
          })
        }
      })
      .catch(() => {
        this.$ui.alert({
          title: this.$t('Error'),
          content: this.$t('AccountEndpoints.failedMessage')
        })
      })
      .finally(() => {
        this.isTesting = false
      })
    }
  },
  computed: {
    configAccess() {
      return this.config || {}
    },
    isCreating() {
      return this.editId === -1;
    },
    // Whether the current configuration is valid against the provider.
    isValid() {
      var provider = this.provider;
      if (provider && this.config && this.config.name) {
        for (var i = 0, len = provider.fields.length; i < len; i++) {
          var field = provider.fields[i];
          var value = this.config[field.key];
          if (!this.validateField(field, value)) {
            return false;
          }
        }
        return true;
      }
      if (this.isEditingCustom) {
        return true;
      }
      return false;
    },
    isValidHttp() {
      if (this.config && this.config.name) {
        if (!this.config.url) {
          return false;
        }
        return true;
      }
      return false;
    },
    isValidFtp() {
      if (this.config && this.config.name) {
        if (!this.config.hostname || !this.config.username || !this.config.password || !this.config.updateSpeed || !this.config.directory) {
          return false;
        }
        return true;
      }
      return false;
    },
    // The provider definition for the list being edited.
    provider() {
      return this.providers[this.config && this.config.espName] || null
    },
    espOptions() {
      const rv = []
      for (const k of Object.keys(this.providers)) {
        rv.push({
          label: this.providers[k].label && this.$t(this.providers[k].label),
          value: k
        })
      }
      return rv
    },
    espConfigs() {
      var map = {}
      this.endpoints.filter((endpoint) => { 
        return endpoint.type === 'ESP' 
      }).forEach((c) => {
        map[c.id] = JSON.parse(c.config)
      })
      return map
    },
    formattedEspConfigs() {
      const rv = []
      for (const k of Object.keys(this.espConfigs)) {
        rv.push({
          id: k,
          integrationName: this.espConfigs[k].name,
          providerName: this.getProviderLabel(this.espConfigs[k].espName)
        })
      }
      return rv
    },
    formattedHttpConfigs() {
      const rv = []
      for (const k of Object.keys(this.httpConfigs)) {
        rv.push({
          id: k,
          integrationName: this.httpConfigs[k].name,
          url: this.httpConfigs[k].url
        })
      }
      return rv
    },
    formattedFtpConfigs() {
      const rv = []
      for (const k of Object.keys(this.ftpConfigs)) {
        rv.push({
          id: k,
          integrationName: this.ftpConfigs[k].name,
          url: this.ftpConfigs[k].url
        })
      }
      return rv
    },
    httpConfigs() {
      var map = {};
      this.endpoints.filter((endpoint) => { 
        return endpoint.type === 'HTTP' 
      }).forEach((c) => {
        map[c.id] = JSON.parse(c.config);
      });
      return map;
    },
    ftpConfigs() {
      var map = {};
      this.endpoints.filter((endpoint) => {
        return endpoint.type === 'FTP' 
      }).forEach((c) => {
        map[c.id] = JSON.parse(c.config);
      });
      return map;
    },
    nameConflict() {
      const config = this.config;
      const editId = this.editId;
      if (config && config.name) {
        for (const endpoint of this.endpoints) {
          const other = JSON.parse(endpoint.config);
          // console.log(endpoint.id, editId, other.name, config.name)
          if (endpoint.id !== editId && other.name.trim().toLowerCase() === config.name.trim().toLowerCase()) {
            return this.$t('AccountEndpoints.This name is already used by another list');
          }
        }
      }
      return '';
    },
    isEditingCustom() {
      return this.config && this.config.espName === 'CUSTOM';
    }
  },
  components: {
  }
}

</script>

<style lang="scss">
#AccountEndpoints {
}
</style>